import { getToken } from './auth'
import { apiConfig } from './config'
import { ApiError } from './errors'
import {
  DefaultService as AiDefaultService,
  ResultsService as AiResultsService,
  TaxonomyService as AiTaxonomyService,
} from './generated/ai'
import { client as aiClient } from './generated/ai/client.gen'
import { DataAdminService } from './generated/data-admin'
import { client as dataAdminClient } from './generated/data-admin/client.gen'
import {
  AcceptedFactsService,
  BatchesService,
  CompareService,
  CoverageService,
  DataDictionaryService,
  EntitiesService,
  EnumsService,
  ErrorsService,
  EstimatedService,
  DefaultService as FactsDefaultService,
  LabelsService as FactsLabelsService,
  ResultsService as FactsResultsService,
  RevenueService as FactsRevenueService,
  SemanticSearchService as FactsSemanticSearchService,
  FactsService,
  FeedbackService,
  HoldingsService,
  KeywordsService,
  KpiResultsService,
  KpisService,
  MetricsService,
  PortfoliosService,
  QuestionsService,
  ReportingStandardsService,
  SearchService,
  SegmentsService,
  SnapshotsService,
  SourcesService,
  StatsService,
  TasksService,
  UnitsService,
} from './generated/facts'
import { client as factsClient } from './generated/facts/client.gen'
import {
  ActivitiesService,
  ActivityCostsService,
  DatapointsService,
  EstimationsService,
  DefaultService as ModellingDefaultService,
  TaxonomiesService,
  WorkbooksService,
} from './generated/modelling'
import { client as modellingClient } from './generated/modelling/client.gen'
import {
  DebugService,
  GroupsService,
  MetricConfigService,
  ServiceAccountsService,
  SyncService,
  DefaultService as UsersDefaultService,
  LabelsService as UsersLabelsService,
  UsersService,
} from './generated/users'
import { client as usersClient } from './generated/users/client.gen'
import { promotedFeatureIsEnabled } from './promotedFeature'

type GeneratedClient = typeof factsClient

// The generated clients throw the parsed response body rather than an Error, so
// normalise it into ApiError here to give consumers a single error type to catch.
const configureClient = (client: GeneratedClient, baseUrl: string, sendPromoted = false) => {
  client.setConfig({
    auth: () => getToken(false),
    baseUrl,
  })
  if (sendPromoted) {
    client.interceptors.request.use((request) => {
      // `append`, not `set` — callers that already pass their own x-np-feature
      // (e.g. 'snapshot-attribution') must keep it.
      if (promotedFeatureIsEnabled()) {
        request.headers.append('x-np-feature', 'promoted')
      }
      return request
    })
  }
  client.interceptors.error.use((error, response) => {
    // Non-HTTP failures (a rejected token fetch, an offline `TypeError: Failed to
    // fetch`, a malformed JSON body) also land here already carrying a usable
    // message, so leave them alone rather than burying them in `body`.
    if (error instanceof Error) {
      return error
    }
    return new ApiError({ body: error, status: response?.status, url: response?.url })
  })
}

configureClient(factsClient, apiConfig.factsHost, true)
configureClient(modellingClient, apiConfig.modellingHost)
configureClient(usersClient, apiConfig.usersHost)
configureClient(aiClient, apiConfig.aiHost)
configureClient(dataAdminClient, apiConfig.dataAdminHost)

// Exported for the few callers that need the client itself rather than an SDK method:
// requests to runtime-chosen URLs, and pointing Cypress at the environment under test.
export { factsClient, usersClient }

export const GeneratedFactApi = {
  acceptedFacts: AcceptedFactsService,
  batches: BatchesService,
  compare: CompareService,
  coverage: CoverageService,
  dataDictionary: DataDictionaryService,
  default: FactsDefaultService,
  entities: EntitiesService,
  enums: EnumsService,
  errors: ErrorsService,
  estimated: EstimatedService,
  facts: FactsService,
  feedback: FeedbackService,
  holdings: HoldingsService,
  keywords: KeywordsService,
  kpiResults: KpiResultsService,
  kpis: KpisService,
  labels: FactsLabelsService,
  metrics: MetricsService,
  portfolios: PortfoliosService,
  questions: QuestionsService,
  reportingStandards: ReportingStandardsService,
  results: FactsResultsService,
  revenue: FactsRevenueService,
  search: SearchService,
  segments: SegmentsService,
  semanticSearch: FactsSemanticSearchService,
  snapshots: SnapshotsService,
  sources: SourcesService,
  stats: StatsService,
  tasks: TasksService,
  units: UnitsService,
}

export const GeneratedModellingApi = {
  activities: ActivitiesService,
  activityCosts: ActivityCostsService,
  datapoints: DatapointsService,
  default: ModellingDefaultService,
  estimations: EstimationsService,
  taxonomies: TaxonomiesService,
  workbooks: WorkbooksService,
}

export const GeneratedUsersApi = {
  debug: DebugService,
  default: UsersDefaultService,
  groups: GroupsService,
  labels: UsersLabelsService,
  metricConfig: MetricConfigService,
  serviceAccounts: ServiceAccountsService,
  sync: SyncService,
  users: UsersService,
}

export const GeneratedAiApi = {
  default: AiDefaultService,
  results: AiResultsService,
  taxonomy: AiTaxonomyService,
}

export const GeneratedDataAdminApi = {
  dataAdmin: DataAdminService,
}
